import React, { useMemo, useState } from 'react'
import { Card, Empty } from 'antd'
import { useSelector } from 'react-redux'
import { getColorByIndex } from '../../../../../utils/colors.helper'
import ComponentWithEditor from '../../../components/file-drawer/components/ComponentWithEditor'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import { FormContent } from '../../../components/semantic-pattern-card/components/FormContent'

/**
 * Right-side detail view displaying the full content of a single pattern
 * Shows form fields, code editor with highlights, and all pattern metadata
 */
const PatternDetailView = ({
    pattern,
    form,
    criteriaUpdating = [],
    updateCriteriaFields,
    onRegenerateAndProject,
}) => {
    const language = useSelector(assignmentSelectors.language)
    const [focusedCriterion, setFocusedCriterion] = useState(null)

    // Watch for color changes in the form
    const selectedColor = pattern ? form?.getFieldValue(`color-${pattern.id}`) : null

    // Build patterns list for code editor highlighting
    const patternsList = useMemo(() => {
        if (!pattern?.projection?.length) return []

        return pattern.projection.reduce((acc, p) => {
            if (p.matched_snippets) {
                p.matched_snippets.forEach(snippet => {
                    acc.push({
                        motifId: pattern.id,
                        startRow: snippet.lineno,
                        endRow: snippet.end_lineno,
                        color: getColorByIndex(selectedColor ?? 0).value,
                        tooltip: `Hit by criterion: "${p.criterion}"`,
                        hideId: true,
                    })
                })
            }
            return acc
        }, [])
    }, [pattern?.projection, selectedColor, pattern?.id])

    // Build selected snippets for focused criterion
    const selectedSnippets = useMemo(() => {
        if (!focusedCriterion || !pattern?.projection?.length) return []

        const projection = pattern.projection.find(p => p.criterion === focusedCriterion)
        return projection?.matched_snippets?.map(snippet => ({
            start: snippet.lineno,
            end: snippet.end_lineno,
        })) || []
    }, [focusedCriterion, pattern?.projection])

    // Merge criteria with projection data
    const criteriaWithProjection = useMemo(() => {
        if (!pattern?.criteria || !pattern?.projection) {
            return pattern?.criteria || []
        }

        return pattern.criteria.map(criterion => {
            const projection = pattern.projection.find(p => p.criterion === criterion.value)
            if (projection) {
                return {
                    ...criterion,
                    reasoning: projection.reasoning,
                }
            }
            return criterion
        })
    }, [pattern?.criteria, pattern?.projection])

    if (!pattern) {
        return (
                <Empty
                    description="Select a pattern to view details"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
        )
    }

    return (
        <div style={{ height: '100%', overflow: 'auto', padding: '0 16px' }}>
            <ComponentWithEditor
                name={pattern.file_name}
                content={pattern.file_content}
                language={language}
                patternsList={patternsList}
                selectedSnippets={selectedSnippets}
                showEditor={!!pattern.file_content}
            >
                <FormContent
                    semanticPattern={pattern}
                    criteriaWithProjection={criteriaWithProjection}
                    criteriaThatAreWaitingForRegenerate={criteriaUpdating}
                    setCriteriaFields={updateCriteriaFields}
                    onRegenerateAndProject={onRegenerateAndProject}
                    setFocusedCriterion={setFocusedCriterion}
                />
            </ComponentWithEditor>
        </div>
    )
}

export default React.memo(PatternDetailView)
